import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateSr } from "@normax-performance/shared-format";
import { DateField } from "../../../shared-ui/primitives";
import { computePeriodDayCount, computeQuickPeriod, type AnalyticsPeriodRange, type QuickPeriodKey } from "../period";

export interface PeriodControlProps {
  period: AnalyticsPeriodRange;
  onChange: (period: AnalyticsPeriodRange) => void;
  /** Which quick-pick, if any, matches the CURRENT period exactly — null when the period was reached via custom range or no longer matches any preset. */
  activeQuickKey: QuickPeriodKey | null;
}

const QUICK_KEYS: QuickPeriodKey[] = ["yesterday", "thisWeek", "thisMonth", "previousMonth"];

/**
 * The page-header period control (docs/.../FORENSIC_PRODUCT_BRIEF.md §6 +
 * this slice's spec §5) — a segmented preset-pill group (visual precedent:
 * design-preview's `.period-bar .preset-group`, already in production
 * tokens) plus an inline custom range using the existing `DateField`
 * primitive. Deliberately NOT a filter panel — one row, part of the page
 * header.
 */
export function PeriodControl({ period, onChange, activeQuickKey }: PeriodControlProps) {
  const { t } = useTranslation("analytics");
  const { t: tCommon } = useTranslation("common");
  const [customOpen, setCustomOpen] = useState(activeQuickKey === null);
  const [draftFrom, setDraftFrom] = useState(period.from);
  const [draftTo, setDraftTo] = useState(period.to);
  const rangeInvalid = draftFrom !== "" && draftTo !== "" && draftFrom > draftTo;

  // Re-sync whenever the period actually changes for ANY reason — including
  // an external change (e.g. the empty-state's "Show yesterday" quick
  // action calling onChange directly, bypassing this component's own
  // selectQuick/applyCustom handlers) — so the preset row never shows a
  // stale "Prilagođeno" highlight after an external period change.
  useEffect(() => {
    setCustomOpen(activeQuickKey === null);
    setDraftFrom(period.from);
    setDraftTo(period.to);
  }, [period.from, period.to, activeQuickKey]);

  function selectQuick(key: QuickPeriodKey) {
    setCustomOpen(false);
    onChange(computeQuickPeriod(key, new Date()));
  }

  function openCustom() {
    setCustomOpen(true);
    setDraftFrom(period.from);
    setDraftTo(period.to);
  }

  function applyCustom() {
    if (rangeInvalid || draftFrom === "" || draftTo === "") return;
    onChange({ from: draftFrom, to: draftTo });
  }

  const dayCount = computePeriodDayCount(period.from, period.to);

  return (
    <div className="nx-period-control">
      <div className="nx-period-control__presets" role="group" aria-label={t("period.quickLabels.custom")}>
        {QUICK_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={["nx-period-control__preset", activeQuickKey === key ? "nx-period-control__preset--active" : ""]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={activeQuickKey === key}
            onClick={() => selectQuick(key)}
          >
            {t(`period.quickLabels.${key}`)}
          </button>
        ))}
        <button
          type="button"
          className={["nx-period-control__preset", customOpen ? "nx-period-control__preset--active" : ""]
            .filter(Boolean)
            .join(" ")}
          aria-pressed={customOpen}
          onClick={openCustom}
        >
          {t("period.quickLabels.custom")}
        </button>
      </div>

      {customOpen ? (
        <div className="nx-period-control__custom">
          <DateField
            label={t("period.from")}
            value={draftFrom}
            onChange={setDraftFrom}
            invalidFormatMessage={tCommon("dateField.invalidFormat")}
            calendarButtonLabel={tCommon("dateField.openCalendar")}
            previousMonthLabel={tCommon("dateField.previousMonth")}
            nextMonthLabel={tCommon("dateField.nextMonth")}
          />
          <DateField
            label={t("period.to")}
            value={draftTo}
            onChange={setDraftTo}
            invalidFormatMessage={tCommon("dateField.invalidFormat")}
            calendarButtonLabel={tCommon("dateField.openCalendar")}
            previousMonthLabel={tCommon("dateField.previousMonth")}
            nextMonthLabel={tCommon("dateField.nextMonth")}
          />
          <button
            type="button"
            className="nx-period-control__preset nx-period-control__preset--active"
            onClick={applyCustom}
            disabled={rangeInvalid || draftFrom === "" || draftTo === ""}
          >
            {t("period.apply")}
          </button>
          {rangeInvalid ? <p className="nx-period-control__error">{t("period.invalidRange")}</p> : null}
        </div>
      ) : null}

      <span className="nx-period-control__recap">
        {formatDateSr(period.from)} — {formatDateSr(period.to)} · {t("period.dayCount", { count: dayCount })}
      </span>
    </div>
  );
}
